import { Api, IConfig } from '@estee/elc-service';
import { IHttpTransport } from '@estee/elc-universal-utils';
import { ConfigStore } from '../service-setup/ConfigStore';

export interface ISignInResponse {
    token_type: string;
    access_token: string;
    expires_in: number;
    userId: string;
    userEmail: string;
    userRole: string;
    userCreationDate: string;
}

export interface ISessionData {
    access_token: string | null;
    expires: number | null;
    spa_urls: string[] | null;
    user_id: string | null;
}

interface IResult<T extends ISessionData> {
    code: number;
    data: ISessionData;
    message: string;
}

export interface ISignInApiSdkConfig extends IConfig {
    baseUrl: string;
    configStore: ConfigStore;
}

const { name, version } = __serviceInfo__;

export class SignInApiSdk extends Api {
    private readonly authApiUrl: string;
    private readonly baseUrl: string;
    private readonly sessionMgmtUrl: string;

    constructor(config: ISignInApiSdkConfig) {
        super(config, name, version);
        this.authApiUrl = 'stardust-account-proxy-v0/oauth';

        this.baseUrl = config.baseUrl;

        this.sessionMgmtUrl = `${this.baseUrl ?? ''}/stardust-account-session-mgt-v0`;
    }

    public get tokenUrl() {
        return `/${this.authApiUrl}/token`;
    }

    public guestTokenRequest = async (): Promise<ISignInResponse> => {
        const response = await this.httpTransport.post<ISignInResponse>({
            url: this.tokenUrl,
            body: {
                grant_type: 'client_credentials'
            }
        });

        return response.data;
    };

    public getLegacyGuestTokenIndependentFromApi = async (): Promise<ISignInResponse> => {
        const url = new URL(this.tokenUrl, this.baseUrl);

        const request: RequestInit = {
            method: 'POST',
            body: JSON.stringify({
                grant_type: 'client_credentials'
            }),
            headers: (this.httpTransport as IHttpTransport).getStaticGlobalHeaders()
        };

        const response = await fetch(url.toString(), request);
        const responseJson = await response.json();

        return responseJson as ISignInResponse;
    };

    public getSession = async (): Promise<IResult<ISessionData>> => {
        const response = await fetch(`${this.sessionMgmtUrl}/sessions/current`, {
            credentials: 'include'
        });

        if (!(response.status >= 200 && response.status < 300)) {
            throw response;
        }

        const sessionData = (await response.json()) as IResult<ISessionData>;

        return sessionData;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public signIn = async (signInData: any): Promise<ISignInResponse> => {
        const response = await this.httpTransport.post<ISignInResponse>({
            url: this.tokenUrl,
            body: signInData
        });

        return response.data;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public signOut = async (signOutData: any) => {
        return this.httpTransport.post({
            url: `/${this.authApiUrl}/revoke`,
            body: signOutData
        });
    };
}
